import { type ChangeEvent, type DragEvent, PureComponent } from "react";
import classNames from "classnames";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { IconSize } from "../../common/components/icon/IconSize";
import { type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { BulkUploadSource, BulkUploadState, type Msg } from "../models";
import { Button } from "~/common/components/Button/Button";

interface FileUploadSectionProps extends WithTranslation {
  onFileSelected: (file: File) => void;
  onFileError?: () => void;
  state: BulkUploadState;
  source: BulkUploadSource;
  messages: Msg[];
}

interface FileUploadSectionState {
  isDragging: boolean;
}

class FileUploadSection extends PureComponent<FileUploadSectionProps, FileUploadSectionState> {
  inputRef: HTMLInputElement | null = null;
  formRef: HTMLFormElement | null = null;

  state = {
    isDragging: false,
  };

  dragenter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === "file") {
      this.setState({ isDragging: true });
    }
  };

  dragover = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  dragleave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    this.setState({ isDragging: false });
  };

  drop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    this.setState({ isDragging: false });

    const file: File | undefined = e.dataTransfer.files[0];
    if (file) {
      this.props.onFileSelected(file);
    }
  };

  selectFile = () => {
    if (this.inputRef) {
      this.inputRef.click();
    }
  };

  onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      this.props.onFileSelected(file);
    }
    if (this.formRef) {
      this.formRef.reset();
    }
  };

  render() {
    const { state, source, messages, t } = this.props;
    const { isDragging } = this.state;

    const btnDisabled = state === BulkUploadState.UPLOADING;

    let iconType = IconType.FilePlus;
    let disableInteractions = false;
    let isSuccess = false;
    if (source === BulkUploadSource.FILE) {
      if (state === BulkUploadState.UPLOAD_SUCCESS) {
        iconType = IconType.FileCheck;
        isSuccess = true;
      } else if (state === BulkUploadState.UPLOADING) {
        iconType = IconType.FileClock;
        disableInteractions = true;
      } else if (state === BulkUploadState.ERROR) {
        iconType = IconType.FileError;
      } else if (state === BulkUploadState.LOADING_CART) {
        disableInteractions = true;
      }
    }

    const isInvalid = state === BulkUploadState.ERROR && source === BulkUploadSource.FILE;
    const hasErrorMessage = isInvalid && messages.length !== 0;
    const colorHelperGreen = !isInvalid && messages.length !== 0;

    const containerCls = classNames("scha__bulk-add__item-drop-area", {
      "is-dragging": isDragging,
      "is-invalid": isInvalid,
    });
    const helperCls = classNames("scha__bulk-add__helper", {
      "scha__bulk-add__helper--error": hasErrorMessage,
      "f-green": colorHelperGreen,
    });

    const helperMsg = hasErrorMessage ? messages.map((msg) => msg.label) : t("bulkUpload.file.helper");
    return (
      <div className={"d-flex flex-direction-column w-100 flex-justify-between h-100"}>
        <div className="scha__bulk-add__item-drop-area--wrapper">
          <div className={containerCls}>
            <Icon type={iconType} size={IconSize.FONT_50} />
            <div
              className={"scha__bulk-add__item-drop-catcher"}
              onDragEnter={disableInteractions ? undefined : this.dragenter}
              onDragLeave={disableInteractions ? undefined : this.dragleave}
              onDrop={disableInteractions ? undefined : this.drop}
              onDragOver={disableInteractions ? undefined : this.dragover}
            />
            {isSuccess && (
              <p className={"scha__bulk-add__loading-spinner title-medium f-carbon f-center mb-2"}>
                {t("bulkUpload.file.successLabel")}
              </p>
            )}
          </div>
          <form style={{ display: "none" }} ref={(ref) => (this.formRef = ref)}>
            <input type={"file"} ref={(ref) => (this.inputRef = ref)} onChange={this.onFileChange} />
          </form>
          <div className={helperCls}>{helperMsg}</div>
        </div>
        <Button
          size={"medium"}
          disabled={btnDisabled}
          data-test-id={"bulk-upload-btn-upload-file"}
          onClick={this.selectFile}
        >
          {t("bulkUpload.file.button")}
        </Button>
      </div>
    );
  }
}

export default withTranslationBase()(FileUploadSection);
